import type { CSSProperties } from 'react';
import { useCallback } from 'react';
import type { ILoadingAnimationViewProps as ILoadingAnimationViewProperties } from './LoadingAnimationView.interface';
import styles from './LoadingAnimationView.module.css';

enum StyleElement {
    Container = 'Container',
    SegmentItem1 = 'SegmentItem1',
    SegmentItem2 = 'SegmentItem2',
    SegmentItem3 = 'SegmentItem3',
    SegmentItem4 = 'SegmentItem4',
    SegmentItem5 = 'SegmentItem5',
    SegmentItem6 = 'SegmentItem6',
    SegmentItem7 = 'SegmentItem7',
    SegmentItem8 = 'SegmentItem8',
    SegmentItem9 = 'SegmentItem9',
    SegmentItem10 = 'SegmentItem10',
    SegmentItem11 = 'SegmentItem11',
    SegmentItem12 = 'SegmentItem12',
    SegmentContainer1 = 'SegmentContainer1',
    SegmentContainer2 = 'SegmentContainer2',
    SegmentContainer3 = 'SegmentContainer3',
    SegmentContainer4 = 'SegmentContainer4',
    SegmentContainer5 = 'SegmentContainer5',
    SegmentContainer6 = 'SegmentContainer6',
    SegmentContainer7 = 'SegmentContainer7',
    SegmentContainer8 = 'SegmentContainer8',
    SegmentContainer9 = 'SegmentContainer9',
    SegmentContainer10 = 'SegmentContainer10',
    SegmentContainer11 = 'SegmentContainer11',
    SegmentContainer12 = 'SegmentContainer12',
}

const SegmentItems = new Set([
    StyleElement.SegmentItem1,
    StyleElement.SegmentItem2,
    StyleElement.SegmentItem3,
    StyleElement.SegmentItem4,
    StyleElement.SegmentItem5,
    StyleElement.SegmentItem6,
    StyleElement.SegmentItem7,
    StyleElement.SegmentItem8,
    StyleElement.SegmentItem9,
    StyleElement.SegmentItem10,
    StyleElement.SegmentItem11,
    StyleElement.SegmentItem12,
]);

const SegmentContainers = new Set([
    StyleElement.SegmentContainer1,
    StyleElement.SegmentContainer2,
    StyleElement.SegmentContainer3,
    StyleElement.SegmentContainer4,
    StyleElement.SegmentContainer5,
    StyleElement.SegmentContainer6,
    StyleElement.SegmentContainer7,
    StyleElement.SegmentContainer8,
    StyleElement.SegmentContainer9,
    StyleElement.SegmentContainer10,
    StyleElement.SegmentContainer11,
    StyleElement.SegmentContainer12,
]);

export function classNameForElement(
    element: StyleElement,
    properties: ILoadingAnimationViewProperties,
): string {
    const name = element.toString();
    const classLoads = [];
    classLoads.push(styles[name]);

    if (SegmentItems.has(element)) {
        classLoads.push(styles['SegmentItem']);
    }

    if (SegmentContainers.has(element)) {
        classLoads.push(styles['SegmentContainer']);
    }

    return classLoads.join(' ');
}

const _ITEM_APPLICATOR = (properties: ILoadingAnimationViewProperties) => ({
    backgroundColor: properties.color,
});

const _NOOP_APPLICATOR = (properties: ILoadingAnimationViewProperties) => ({});

const PROPS_APPLICATOR: Record<
    StyleElement,
    (properties: ILoadingAnimationViewProperties) => CSSProperties
> = {
    [StyleElement.Container]: (properties: ILoadingAnimationViewProperties) => ({
        opacity: properties.isAnimating ? 1 : 0,
        width: properties.size,
        height: properties.size,
        ...properties.style,
    }),
    [StyleElement.SegmentItem1]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem2]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem3]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem4]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem5]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem6]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem7]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem8]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem9]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem10]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem11]: _ITEM_APPLICATOR,
    [StyleElement.SegmentItem12]: _ITEM_APPLICATOR,
    [StyleElement.SegmentContainer1]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer2]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer3]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer4]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer5]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer6]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer7]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer8]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer9]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer10]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer11]: _NOOP_APPLICATOR,
    [StyleElement.SegmentContainer12]: _NOOP_APPLICATOR,
};

function styleForElement(
    element: StyleElement,
    properties: ILoadingAnimationViewProperties,
): CSSProperties {
    const style = { ...PROPS_APPLICATOR[element](properties) };

    return style;
}

/* Pairings of sub-items for looping over during render */
const _SubItemPairings = [
    [StyleElement.SegmentContainer1, StyleElement.SegmentItem1],
    [StyleElement.SegmentContainer2, StyleElement.SegmentItem2],
    [StyleElement.SegmentContainer3, StyleElement.SegmentItem3],
    [StyleElement.SegmentContainer4, StyleElement.SegmentItem4],
    [StyleElement.SegmentContainer5, StyleElement.SegmentItem5],
    [StyleElement.SegmentContainer6, StyleElement.SegmentItem6],
    [StyleElement.SegmentContainer7, StyleElement.SegmentItem7],
    [StyleElement.SegmentContainer8, StyleElement.SegmentItem8],
    [StyleElement.SegmentContainer9, StyleElement.SegmentItem9],
    [StyleElement.SegmentContainer10, StyleElement.SegmentItem10],
    [StyleElement.SegmentContainer11, StyleElement.SegmentItem11],
    [StyleElement.SegmentContainer12, StyleElement.SegmentItem12],
] as const;

function LoadingAnimationView(properties: ILoadingAnimationViewProperties): JSX.Element {
    const style = useCallback(
        (element: StyleElement) => styleForElement(element, properties),
        [properties],
    );
    const className = useCallback(
        (element: StyleElement) => classNameForElement(element, properties),
        [properties],
    );

    return (
        <div
            style={style(StyleElement.Container)}
            className={`${properties.className ?? ''} ${className(StyleElement.Container)}`}
        >
            {_SubItemPairings.map(([elementContainer, elementItem], index) => (
                <div
                    key={`${index}`}
                    style={style(elementContainer)}
                    className={className(elementContainer)}
                >
                    <div style={style(elementItem)} className={className(elementItem)}></div>
                </div>
            ))}
        </div>
    );
}

export default LoadingAnimationView;
